// Ranks a pre-filtered listing array by relevance to a parsed AI intent.
// Runs entirely in the browser, no network, no latency.
//
// Scoring breakdown (max ~1.05):
//   0.40, style semantic match (AI taxonomy → synonym expansion against DB styles + corpus)
//   0.50, lexical token density across name + shortDescription + styles + city + region
//   0.10, featured flag            (tie-breaker)
//   0.05, contentScore quality     (secondary tie-breaker)

const STOP_WORDS = new Set(["the", "and", "for", "with", "our", "your", "that", "this", "are", "was", "venue", "venues", "wedding"]);

// Maps AI taxonomy style names → synonyms found in real DB styles[] and descriptions.
// Bridges the gap between AI abstract styles and venue-type/descriptor data in the DB.
const STYLE_SYNONYMS = {
  "Rustic Luxe":   ["rustic", "barn", "farmhouse", "countryside", "country house", "estate", "winery", "rural"],
  "Black Tie":     ["ballroom", "grand hotel", "formal", "palazzo", "gala", "opulent"],
  "Romantic":      ["romantic", "intimate", "candlelit", "rose", "garden", "dreamy"],
  "Garden":        ["garden", "botanical", "outdoor", "meadow", "floral", "grounds"],
  "Historic":      ["historic", "heritage", "manor", "abbey", "castle", "palazzo", "medieval", "stately", "listed"],
  "Vineyard":      ["vineyard", "wine", "estate", "winery", "domaine"],
  "Intimate":      ["intimate", "boutique", "private", "cosy", "exclusive"],
  "Coastal":       ["coastal", "beach", "seaside", "island", "overwater", "beachfront", "seafront", "cliffside", "ocean", "sea"],
  "Castle":        ["castle", "fortress", "chateau", "château", "tower", "battlement"],
  "Contemporary":  ["contemporary", "modern", "sleek", "industrial", "urban", "design"],
  "Minimalist":    ["minimalist", "minimal", "clean", "understated", "nordic"],
  "Bohemian":      ["bohemian", "boho", "eclectic", "wildflower", "festival", "free spirit"],
  "Art Deco":      ["art deco", "deco", "gatsby", "1920s", "glamour", "gilded"],
};

function styleMatches(style, listing) {
  if (!style) return false;
  const synonyms = STYLE_SYNONYMS[style] || [style.toLowerCase()];
  const corpus = [
    listing.name || '',
    listing.shortDescription || '',
    listing.city || '',
    listing.region || '',
    ...(Array.isArray(listing.styles) ? listing.styles : []),
  ].join(' ').toLowerCase();
  return synonyms.some(syn => corpus.includes(syn));
}

export function rankListingsByAIIntent(listings, aiIntent, signals = {}) {
  if (!aiIntent || !listings?.length) return listings;

  const { style, styles, services, summary } = aiIntent;
  const styleList = Array.isArray(styles) && styles.length ? styles : (style ? [style] : []);

  const searchTokens = `${styleList.join(' ')} ${services || ''} ${summary || ''}`
    .toLowerCase()
    .split(/[\s,–\-()+]+/)
    .filter(t => t.length > 2 && !STOP_WORDS.has(t));

  if (!searchTokens.length) return listings;

  const scored = listings.map(listing => {
    let score = 0;

    // 1. Style semantic match, any matched style contributes, capped at 0.4
    const matchCount = styleList.filter(s => styleMatches(s, listing)).length;
    if (matchCount > 0) {
      score += Math.min(0.4 * (matchCount / styleList.length) + 0.2, 0.4);
    }

    // 2. Token density: name + shortDescription + styles + city/region
    const corpus = [
      listing.name             || '',
      listing.shortDescription || '',
      listing.city             || '',
      listing.region           || '',
      ...(listing.styles || []),
    ].join(' ').toLowerCase();

    let hits = 0;
    for (const token of searchTokens) {
      if (corpus.includes(token)) hits++;
    }
    score += (hits / searchTokens.length) * 0.5;

    // 3. Behavioral signal boost (clicks + enquiries from lwd_intelligence_events)
    if (signals[listing.id]) score += signals[listing.id];

    // 4. Quality tie-breakers
    if (listing.featured) score += 0.1;
    if (listing.contentScore > 0) score += Math.min(listing.contentScore / 100, 0.05);

    return { ...listing, _aiScore: score };
  });

  scored.sort((a, b) => b._aiScore - a._aiScore);
  for (const l of scored) delete l._aiScore;
  return scored;
}
